import React, { useEffect, useRef, useState } from 'react';

import { routes } from '../../../constants.js/misc';
import { getStorePublicUrl } from '../../../utils/storeUrl';
import { finalizeOnboardingV2 } from '../../../services/onboardingV2';
import { writeOnboardingState } from '../useOnboardingDraft';
import { peekPendingFinalize, clearPendingFinalize } from '../onboardingV2.pending';
import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import SuccessHero from '../components/SuccessHero/SuccessHero';
import StorePreviewCard from '../components/StorePreviewCard/StorePreviewCard';
import { NextSteps, ChecklistRow } from '../components/ChecklistRow/ChecklistRow';
import { Button, TextButton } from '../components/Button/Button';
import Callout from '../components/Callout/Callout';
import { formatMoney } from '../onboardingV2.locale';
import { Actions, Center, Stack, FooterNote } from './steps.styles';

const COPIED_MS = 2000;

const copyText = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fall back below */
    }
    try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
    } catch {
        return false;
    }
};

/** Step 5 · lista: store card, share/view, next steps. Variant A (product) or B (no product). */
const SuccessStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        api,
        analytics,
        storeId,
        storeData,
        onboarding,
        updateOnboarding,
        productPreviewUrl,
        goTo,
        markCompleted,
        resetDemo,
        history,
    } = ctx;

    // Product is created at the end now, so detect it from the captured data, not a productId.
    const hasProduct = !!(onboarding.productId || onboarding.productName) && onboarding.skippedProduct !== true;
    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [storeFailed, setStoreFailed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [liveMessage, setLiveMessage] = useState('');
    const copyTimer = useRef(null);
    // What the signup captured but could only be created with a session (see WhatsAppStep).
    const [pending] = useState(() => (isDemo ? null : peekPendingFinalize(storeId)));
    const [finalizing, setFinalizing] = useState(!!pending);
    const [finalizeFailed, setFinalizeFailed] = useState(false);

    // Create logo, collections (+ photos, subcategories), product (+ photo) and volume pricing.
    // Resilient: partial failures are tracked and shown as a warning, never block this screen.
    useEffect(() => {
        if (!pending) return undefined;
        clearPendingFinalize();
        let cancelled = false;
        finalizeOnboardingV2(api, storeId, pending.data)
            .then((summary) => {
                const patch = {
                    productId: summary.productId || null,
                    collectionIds: summary.collectionIds,
                    pricingTierId: summary.pricingTierId || null,
                };
                if (cancelled) writeOnboardingState(storeId, patch);
                else updateOnboarding(patch);
                if (summary.errors.length) {
                    analytics.track('onboarding_finalize_partial', {
                        store_id: storeId,
                        failed_stages: summary.errors.map((e) => e.stage).join(','),
                    });
                    if (!cancelled) setFinalizeFailed(true);
                }
            })
            .catch((error) => {
                analytics.track('onboarding_finalize_failed', {
                    store_id: storeId,
                    http_status: error?.response?.status ?? null,
                });
                if (!cancelled) setFinalizeFailed(true);
            })
            .finally(() => {
                if (!cancelled) setFinalizing(false);
            });
        return () => {
            cancelled = true;
        };
        // Once, on arrival from the signup.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.getStore(storeId)
            .then((data) => {
                if (!cancelled) setStore(data);
            })
            .catch((error) => {
                analytics.track('onboarding_store_load_failed', {
                    store_id: storeId,
                    http_status: error?.response?.status ?? null,
                });
                if (!cancelled) setStoreFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [api, analytics, storeId]);

    // Viewing the success screen with a product completes the onboarding.
    // Tracked once per onboarding (and per variant), not on every reload or back/forward.
    useEffect(() => {
        const trackedKey = hasProduct ? 'completedTrackedAt' : 'createdTrackedAt';
        if (!onboarding[trackedKey]) {
            analytics.track('onboarding_completed', {
                has_product: hasProduct,
                total_duration_ms: onboarding.accountCreatedAt
                    ? Date.now() - onboarding.accountCreatedAt
                    : null,
            });
            updateOnboarding({ [trackedKey]: Date.now() });
        }
        if (hasProduct) markCompleted();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => window.clearTimeout(copyTimer.current), []);

    const storeName =
        store?.name ||
        onboarding.storeName ||
        storeData.name ||
        (isDemo ? t('SignUpV2.demo.storeName') : '');
    const storeUrl = getStorePublicUrl(store);
    const currency = ctx.currency || storeData.currency || 'COP';

    const onCopy = async () => {
        const ok = await copyText(storeUrl);
        // The intent counts even when the browser blocks the clipboard (`copied: false`).
        analytics.track('store_shared', { channel: 'copy', context: 'onboarding_success', copied: !!ok });
        if (!ok) return;
        setCopied(true);
        setLiveMessage(t('SignUpV2.lista.copiedLive'));
        window.clearTimeout(copyTimer.current);
        copyTimer.current = window.setTimeout(() => {
            setCopied(false);
            setLiveMessage('');
        }, COPIED_MS);
    };

    const onShare = () => {
        const message = t('SignUpV2.lista.shareMessage', { storeName, storeUrl });
        analytics.track('store_shared', { channel: 'whatsapp', context: 'onboarding_success' });
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    };

    const onViewStore = () => {
        analytics.track('store_viewed', { store_url: storeUrl });
    };

    const onFirstProduct = () => {
        analytics.track('onboarding_checklist_clicked', { item: 'first_product' });
        updateOnboarding({ skippedProduct: false });
        goTo('producto');
    };

    const onChecklist = (item) => () => {
        analytics.track('onboarding_checklist_clicked', { item });
        markCompleted();
    };

    const onPanel = () => {
        if (isDemo) {
            resetDemo();
            return;
        }
        markCompleted();
        history.push(routes.homeUrl);
    };

    const product = hasProduct
        ? {
              name: onboarding.productName,
              priceLabel: `${formatMoney(onboarding.productPrice, currency, ctx.moneyLocale)} ${currency}`,
              imageUrl: productPreviewUrl || (pending && pending.productPreviewUrl) || null,
          }
        : null;

    const canUseUrl = !loading && !storeFailed && !!storeUrl;

    const viewStoreButton = (variant) =>
        isDemo ? (
            <Button
                variant={variant}
                iconRight="ri-external-link-line"
                disabled
                aria-describedby="lista-demo-note"
            >
                {t('SignUpV2.lista.viewStore')}
            </Button>
        ) : (
            <Button
                variant={variant}
                iconRight="ri-external-link-line"
                href={storeUrl}
                external
                onClick={onViewStore}
            >
                {t('SignUpV2.lista.viewStore')}
            </Button>
        );

    // Main action pinned at the bottom on phones/tablets, like every other step.
    let primaryCta = null;
    if (!hasProduct) {
        primaryCta = (
            <Button iconRight="ri-arrow-right-line" onClick={onFirstProduct}>
                {t('SignUpV2.lista.firstProductCta')}
            </Button>
        );
    } else if (canUseUrl || loading) {
        primaryCta = (
            <Button variant="whatsapp" iconLeft="ri-whatsapp-fill" onClick={onShare} disabled={!canUseUrl}>
                {t('SignUpV2.lista.share')}
            </Button>
        );
    }

    return (
        <OnboardingLayout
            step={ctx.stepNumberOf('lista')}
            total={ctx.totalSteps}
            stepKey="lista"
            title={hasProduct ? t('SignUpV2.lista.title') : t('SignUpV2.lista.titleNoProduct')}
            isDemo={isDemo}
            liveMessage={liveMessage}
            footer={
                <>
                    {primaryCta}
                    <Center>
                        <TextButton tone="brand" onClick={onPanel} iconRight="ri-arrow-right-line">
                            {t('SignUpV2.lista.goToPanel')}
                        </TextButton>
                    </Center>
                </>
            }
        >
            <SuccessHero
                id="lista-title"
                variant={hasProduct ? 'ready' : 'created'}
                title={hasProduct ? t('SignUpV2.lista.title') : t('SignUpV2.lista.titleNoProduct')}
                subtitle={
                    hasProduct
                        ? t('SignUpV2.lista.subtitle')
                        : t('SignUpV2.lista.subtitleNoProduct')
                }
            />

            <Stack gap={12}>
                <StorePreviewCard
                    loading={loading}
                    name={storeName}
                    storeUrl={canUseUrl ? storeUrl : ''}
                    isActive={!!store?.active}
                    product={product}
                    copied={copied}
                    onCopy={onCopy}
                />
                {finalizing && (
                    <Callout variant="info">
                        <p>{t('SignUpV2.lista.finalizing')}</p>
                    </Callout>
                )}
                {finalizeFailed && (
                    <Callout variant="warning">
                        <p>{t('SignUpV2.lista.finalizePartial')}</p>
                    </Callout>
                )}
                {storeFailed && (
                    <Callout variant="warning">
                        <p>{t('SignUpV2.lista.storeError')}</p>
                    </Callout>
                )}
                {hasProduct && onboarding.photoUploaded === false && (
                    <Callout variant="warning">
                        <p>{t('SignUpV2.lista.photoFailed')}</p>
                    </Callout>
                )}
            </Stack>

            <Actions>
                {(canUseUrl || loading) && viewStoreButton('secondary')}
                {isDemo && (canUseUrl || loading) && (
                    <FooterNote id="lista-demo-note">{t('SignUpV2.demo.noWrites')}</FooterNote>
                )}
            </Actions>

            <NextSteps title={t('SignUpV2.lista.nextSteps')}>
                {!hasProduct && (
                    <ChecklistRow
                        highlight
                        icon="ri-add-line"
                        title={t('SignUpV2.lista.checklist.firstProduct')}
                        subtitle={t('SignUpV2.lista.checklist.firstProductSub')}
                        to={
                            isDemo
                                ? '/sign-up?signup=v2&demo=1&step=producto'
                                : '/onboarding/producto'
                        }
                        onClick={(event) => {
                            event.preventDefault();
                            onFirstProduct();
                        }}
                    />
                )}
                <ChecklistRow
                    icon="ri-palette-line"
                    title={t('SignUpV2.lista.checklist.logo')}
                    subtitle={t('SignUpV2.lista.checklist.logoSub')}
                    to={routes.store}
                    disabled={isDemo}
                    onClick={onChecklist('logo')}
                />
                <ChecklistRow
                    icon="ri-bank-card-line"
                    title={t('SignUpV2.lista.checklist.payments')}
                    subtitle={t('SignUpV2.lista.checklist.paymentsSub')}
                    to={routes.paymentProviders}
                    disabled={isDemo}
                    onClick={onChecklist('payments')}
                />
                <ChecklistRow
                    icon="ri-shopping-bag-3-line"
                    title={t('SignUpV2.lista.checklist.moreProducts')}
                    subtitle={t('SignUpV2.lista.checklist.moreProductsSub')}
                    to={routes.productsTable}
                    disabled={isDemo}
                    onClick={onChecklist('more_products')}
                />
            </NextSteps>
        </OnboardingLayout>
    );
};

export default SuccessStep;
